/**
 * The suggested score of a round, from the cards of the side judges, and the
 * majority of the round outcomes (#599).
 *
 * LMF policy (CLAUDE.md § IFMA Competition Rules): the jury is the final
 * scorer, and the cards are guidance. A total that the jury typed replaces
 * the suggestion in every place.
 *
 * The rule (operator, 2026-10-14) is an LMF rule. It follows the five-judge
 * rule of IFMA v3.057 §29.4.6, which decides a draw of the Real Time Scoring
 * System: the highest and the lowest total go, and the other three give the
 * average. IFMA has no such rule for the cards of a round. With 4 or 5
 * cards, remove the highest and the lowest card for each corner, and average
 * the other cards. With 3 cards or fewer, the plain average, as before #599:
 * a past bout with 3 cards keeps its totals. The average rounds half up, as
 * ROUND() of Postgres does for a positive number.
 *
 * The majority follows IFMA v3.057 §30.1 ("a majority of the Judges", for
 * the decision of a bout): a side wins the round when more than half of the
 * cards give it the round. A 2–2 split of four cards has no majority, and the
 * jury decides.
 *
 * Two copies: backend/src/lib/roundSuggestion.ts and
 * frontend/src/utils/roundSuggestion.ts. round-suggestion-599.spec.ts runs
 * both on the same cases. Change them together.
 */

export type RoundOutcome = 'red' | 'blue' | 'even';

/** The suggested points of one corner, from the points of each card. NULL with no card. */
export function suggestPoints(points: readonly number[]): number | null {
  if (!points.length) return null;
  const sorted = [...points].sort((a, b) => a - b);
  const used = sorted.length >= 4 ? sorted.slice(1, -1) : sorted;
  return Math.round(used.reduce((sum, p) => sum + p, 0) / used.length);
}

/** The strict majority of the round outcomes: the side that more than half of the cards give. */
export function roundMajority(outcomes: readonly RoundOutcome[]): { side: RoundOutcome | null; count: number; total: number } {
  const total = outcomes.length;
  const counts: Record<RoundOutcome, number> = { red: 0, blue: 0, even: 0 };
  for (const o of outcomes) counts[o]++;
  for (const side of ['red', 'blue', 'even'] as const) {
    if (counts[side] * 2 > total) return { side, count: counts[side], total };
  }
  return { side: null, count: 0, total };
}
